import React, { useEffect, useState, useCallback } from "react";
import axios from "../utils/axiosInstance";
import { Warning } from "@phosphor-icons/react";
import { ChatBadgeContext } from "../contexts/ChatBadgeContext";

/**
 * Global overlay + heartbeat + chat-unread badge provider.
 * - Fires POST /api/chat/heartbeat every 30 sec while user is authenticated.
 * - Polls GET /api/chat/pending-pilne every 15 sec.
 * - Polls GET /api/chat/unread-count every 15 sec — exposed via ChatBadgeContext.
 * - Renders a full-screen modal if any unacknowledged pilne message exists.
 */
export default function PresenceLayer({ user, children }) {
  const [pending, setPending] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadPending = async () => {
    try {
      const res = await axios.get("/api/chat/pending-pilne");
      setPending(res.data || []);
    } catch { /* ignore */ }
  };

  const loadUnread = useCallback(async () => {
    try {
      const res = await axios.get("/api/chat/unread-count");
      setUnread(res.data?.unread || 0);
    } catch { /* ignore */ }
  }, []);

  const heartbeat = async () => {
    try { await axios.post("/api/chat/heartbeat"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    heartbeat();
    loadPending();
    loadUnread();
    const hb = setInterval(heartbeat, 30000);
    const pl = setInterval(loadPending, 15000);
    const un = setInterval(loadUnread, 15000);
    return () => { clearInterval(hb); clearInterval(pl); clearInterval(un); };
  }, [user, loadUnread]);

  const ack = async (id) => {
    try {
      await axios.post(`/api/chat/messages/${id}/ack`);
      setPending((prev) => prev.filter((m) => m.id !== id));
    } catch { /* ignore */ }
  };

  const badgeValue = { unread, refresh: loadUnread };

  return (
    <ChatBadgeContext.Provider value={badgeValue}>
      {children}
      {user && pending.length > 0 && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" data-testid="pilne-overlay">
          <div className="bg-white rounded-md shadow-2xl max-w-lg w-full p-8 border-4 border-red-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Warning className="w-7 h-7 text-red-600" weight="fill" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-red-600">PILNE</div>
                <div className="text-sm font-semibold text-foreground">Wiadomość od {pending[0].author_name}</div>
              </div>
            </div>
            <div className="text-lg text-foreground whitespace-pre-wrap py-4 border-y border-border" data-testid="pilne-content">
              {pending[0].content}
            </div>
            <button
              onClick={() => ack(pending[0].id)}
              className="w-full mt-5 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-md font-semibold"
              data-testid="pilne-ack"
            >
              OK — Zapoznałem się
            </button>
            {pending.length > 1 && (
              <div className="text-center text-xs text-muted-foreground mt-3">
                Pozostało {pending.length - 1} pilnych wiadomości
              </div>
            )}
          </div>
        </div>
      )}
    </ChatBadgeContext.Provider>
  );
}
